import type { FC, MouseEventHandler } from 'react';
import { useEffect, useState } from 'react';
import Icon from '@components/Icon';
import { useMenu } from '@components/menu';
import { Show } from '@components/Show';
import {
  sidebarSectionChevronClasses,
  sidebarSectionIconClasses,
  sidebarSectionRowClasses,
  sidebarSectionWrapperClasses,
} from '@ContractBuilder/components/shared-sidebar';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useNavigation } from '@ContractBuilder/modules/navigation/context';
import { DatapointsProgressIndicator } from '@ContractBuilder/modules/navigation/modules/nav-section/views/DatapointsProgressIndicator';
import { NavSectionBlocks } from '@ContractBuilder/modules/navigation/modules/nav-section/views/NavSectionBlocks';
import { SectionMenu } from '@ContractBuilder/modules/navigation/modules/nav-section/views/SectionMenu';
import { SectionTitle } from '@ContractBuilder/modules/navigation/modules/nav-section/views/SectionTitle';
import { selectIsAnyMultiSelectActive, useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import type { Section } from '@ContractBuilder/types';
import { areSectionBlocksValid } from '@ContractBuilder/utils/block-validation';
import { useBlockHighlight } from '@ContractBuilder/utils/use-block-highlight';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon, ChevronRightIcon, SwitchVerticalIcon as DragAndDropIcon } from '@heroicons/react/outline';
import { ArtificialCustomEvent, useCustomEventListener } from '@providers/events';
import { getSortedEntries, isNonNullish } from '@root/helpers';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import { isEndorsementViewPath } from '@utils/app-paths';
import clsx from 'clsx';

const visibleOnHoverClasses = ['group-hover:opacity-100', 'opacity-0'];

interface NavSectionProps {
  activeSection?: string;
  forceOpen?: boolean;
  isDragDisabled: boolean;
  section: Section;
  shouldDisableOperations: boolean;
}

export const NavSectionController: FC<NavSectionProps> = ({
  activeSection,
  forceOpen = false,
  isDragDisabled,
  section,
  shouldDisableOperations,
}) => {
  const { blocks, id: sectionId } = section;

  const [open, setOpen] = useState(false);
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const isAnyMultiSelectActive = useMultiSelectStore(selectIsAnyMultiSelectActive);
  const isEndorsementView = isEndorsementViewPath();
  const { activeId } = useNavigation();

  const { isOpen: isMenuOpen, onClose: onMenuClose, onOpen: onMenuOpen } = useMenu();

  useEffect(() => {
    if (shouldDisableOperations) {
      onMenuClose();
    }
  }, [shouldDisableOperations, onMenuClose]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionId,
    disabled: isDragDisabled,
    data: { type: 'section', section },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sortedBlocks = getSortedEntries(section.layout, section.blocks);

  useCustomEventListener(ArtificialCustomEvent.ToggleExpandSections, (value) => setOpen(value));

  useEffect(() => {
    if (editingBlockId) {
      const belongsToSection = section.blocks.some((block) => block.id === editingBlockId);

      if (belongsToSection) {
        setOpen(true);
      }
    }
    // eslint-disable-next-line -- We only want to open the section when the editingBlockId changes, not when the blocks get updated
  }, [editingBlockId]);

  const blocksLength = blocks?.length ?? 0;
  const areBlocksEmpty = blocksLength === 0;

  useEffect(() => {
    if (blocksLength === 0 && !activeId) {
      setOpen(false);
    }
  }, [blocksLength, activeId]);

  const closeList: MouseEventHandler<SVGSVGElement> = (event) => {
    event.stopPropagation();
    return setOpen(false);
  };

  const openList: MouseEventHandler<SVGSVGElement> = (event) => {
    event.stopPropagation();

    if (blocksLength) {
      setOpen(true);
    }
  };

  const { onClick, onMouseEnter, onMouseLeave } = useBlockHighlight();
  const IconComponent = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <li ref={setNodeRef} style={style} {...attributes} id={`${sectionId}-draggable`}>
      <div className={clsx(sidebarSectionWrapperClasses(), 'mx-auto')}>
        <div
          className={sidebarSectionRowClasses({ interactive: !areBlocksEmpty })}
          onClick={() => scrollIntoView(sectionId)}
          title={areBlocksEmpty ? 'Section is empty. First create block in this section' : 'Click to scroll to section'}
        >
          <IconComponent
            className={sidebarSectionChevronClasses({ interactive: !areBlocksEmpty })}
            onClick={open ? closeList : openList}
          />
          <div className="relative">
            <Icon
              name="layout"
              className={sidebarSectionIconClasses({
                active: activeSection === sectionId,
                disabled: areBlocksEmpty,
              })}
            />
            <Show when={!isEndorsementView}>
              <DatapointsProgressIndicator isValid={areSectionBlocksValid(section)} />
            </Show>
          </div>
          <SectionTitle title={section.label} isDimmed={areBlocksEmpty} />
          <div
            className={clsx(
              'relative inline-flex items-center',
              (shouldDisableOperations || isAnyMultiSelectActive) && 'pointer-events-none opacity-30',
            )}
          >
            <Show
              when={(isNonNullish(listeners) && !isDragDisabled) || isAnyMultiSelectActive || shouldDisableOperations}
            >
              <div
                className={clsx(
                  'group-hover:text-info-800 h-6 w-6',
                  visibleOnHoverClasses,
                  !isAnyMultiSelectActive && 'cursor-grab active:cursor-grabbing',
                )}
                data-testid="nav-section-drag-handle"
                {...(isAnyMultiSelectActive ? {} : listeners)}
              >
                <DragAndDropIcon className="h-6 w-6" />
              </div>
            </Show>
            <SectionMenu
              isOpen={isMenuOpen}
              onClose={onMenuClose}
              onOpen={onMenuOpen}
              section_id={sectionId}
              className={clsx(
                visibleOnHoverClasses,
                'group-hover:text-info-800 cursor-pointer',
                isMenuOpen ? 'text-info-800 opacity-100' : 'text-info-400',
              )}
            />
          </div>
        </div>
        {(open || forceOpen) && (
          <NavSectionBlocks
            blocks={sortedBlocks}
            isDragDisabled={shouldDisableOperations || isDragDisabled}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            sectionId={sectionId}
            shouldDisableOperations={shouldDisableOperations}
          />
        )}
      </div>
    </li>
  );
};
